import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useFormikContext } from 'formik'

import { useDeleteQuestionFileV2ApplyMutation, usePutQuestionV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import {
    ApplyV2CustomFieldTypeConst,
    type ApplyV2FileAttachment,
    type ApplyV2Question,
    type ApplyV2TempBase64File,
    type ApplyV2TempCourseFile,
    type AttributesCheckboxesForm,
    type ApplyV2QuestionsFormValues,
} from '@core/app/types/applyV2Type'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2DatePickerInput from '@core/components/ApplyDialogV2/ApplyV2DatePickerInput'
import ApplyV2InputCheckbox from '@core/components/ApplyDialogV2/ApplyV2InputCheckbox'
import ApplyV2InputNumber from '@core/components/ApplyDialogV2/ApplyV2InputNumber'
import ApplyV2InputRadio from '@core/components/ApplyDialogV2/ApplyV2InputRadio'
import ApplyV2InputRate from '@core/components/ApplyDialogV2/ApplyV2InputRate'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2QuestionsList from '@core/components/ApplyDialogV2/ApplyV2QuestionsStep/ApplyV2QuestionsList'
import ApplyV2SelectMultiple from '@core/components/ApplyDialogV2/ApplyV2SelectMultiple'
import ApplyV2SelectSingle from '@core/components/ApplyDialogV2/ApplyV2SelectSingle'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import WarningSolidErrorIcon from '@core/components/icons/WarningSolidErrorIcon'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

interface ApplyV2QuestionsItemProps {
    question: ApplyV2Question
    setIsErrorUpdateAnswer: (value: boolean) => void
    refetchAllQuestions: () => void
    addLoadingItem: (v: string) => void
    removeLoadingItem: (v: string) => void
    setIsQualifyingError: (value: boolean) => void
}

const baseClass = 'ApplyV2QuestionsStep'

function ApplyV2QuestionsItem({
    question,
    refetchAllQuestions,
    setIsErrorUpdateAnswer = (val) => {
        console.log('setIsErrorUpdateAnswer, : ', val)
    },
    addLoadingItem = () => {},
    removeLoadingItem = () => {},
    setIsQualifyingError,
}: ApplyV2QuestionsItemProps) {
    const { onCloseApply, storageData } = useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const formik = useFormikContext<ApplyV2QuestionsFormValues>()
    const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({})
    const [loadingFilesIds, setLoadingFilesIds] = useState<(number | string)[]>([])
    const [updateAnswer, { isLoading, isError, data }] = usePutQuestionV2ApplyMutation()
    const [uploadingFilesCount, setUploadingFilesCount] = useState(0)
    const [deleteFileRequest] = useDeleteQuestionFileV2ApplyMutation()

    useEffect(() => {
        setIsErrorUpdateAnswer(isError)
    }, [isError, setIsErrorUpdateAnswer])

    useEffect(() => {
        const currentTimers = debounceTimers.current

        return () => {
            Object.values(currentTimers).forEach(clearTimeout)
        }
    }, [])

    const questionKey = `${question.type}_${question.questionId}`

    const submitDebounceValue = useCallback(
        (
            field: string,
            value: any,
            options: {
                convertValueToNumber?: boolean
                setFieldValueDefer?: boolean
            } = {
                convertValueToNumber: false,
                setFieldValueDefer: false,
            }
        ) => {
            addLoadingItem(field)
            if (!options.setFieldValueDefer) {
                formik.setSubmitting(true)
                formik.setFieldValue(field, value).then(() => {
                    // console.log(formik.values)
                })
            }
            const questArr = field.split('_')
            // const questionType = questArr[0]
            const questionId = Number(questArr[questArr.length - 1])

            const fieldWithoutId = field.replace(`_${questionId}`, '')

            const attr = {
                bool: fieldWithoutId === 'bool_attribute' ? value : formik.values[`bool_attribute_${questionId}`],
                rating: field.includes('rating_attribute') ? value : formik.values[`rating_attribute_${questionId}`],
                text: fieldWithoutId === 'text_attribute' ? value : formik.values[`text_attribute_${questionId}`],
                shortText:
                    fieldWithoutId === 'shortText_attribute'
                        ? value
                        : formik.values[`shortText_attribute_${questionId}`],
                dropdown:
                    fieldWithoutId === 'dropdowns_attribute'
                        ? value
                        : formik.values[`dropdowns_attribute_${questionId}`],
                date: fieldWithoutId === 'date_attribute' ? value : formik.values[`date_attribute_${questionId}`],
                checkbox:
                    fieldWithoutId === 'checkboxes_attribute'
                        ? (value || formik.values[`checkboxes_attribute_${questionId}`] || [])
                              .filter(({ checked }: { id: number; checked: boolean; rating: number }) => checked)
                              .map(({ id, rating }: { id: number; rating: number }) => ({ id, rating }))
                        : (formik.values[`checkboxes_attribute_${questionId}`] || [])
                              .filter(({ checked }: { id: number; checked: boolean; rating: number | null }) => checked)
                              .map(({ id, rating }: { id: number; rating: number | null }) => ({ id, rating })),
            }

            if (debounceTimers.current[field]) {
                clearTimeout(debounceTimers.current[field])
            }

            debounceTimers.current[field] = setTimeout(
                async (tims) => {
                    try {
                        if (!storageData?.postInitUid) return

                        // console.log(fieldWithoutId, questionId)
                        if (fieldWithoutId.includes('attribute')) {
                            const newTouched = { ...formik.touched } as any

                            Object.keys(formik.values).forEach((key) => {
                                if (key.includes(`attribute_${questionId}`)) {
                                    newTouched[key] = true
                                }
                            })

                            formik.setTouched(newTouched, false)

                            await updateAnswer({
                                uid: storageData.postInitUid,
                                questionId,
                                value: attr,
                            })
                                .unwrap()
                                .then((res) => {
                                    // const { date } = res.data?.answer?.answer?.value ?? {}
                                    // if (date) {
                                    //     formik.setFieldError(`year_date_attribute_${questionId}`, undefined)
                                    //     formik.setFieldError(`month_date_attribute_${questionId}`, undefined)
                                    //     formik.setFieldError(`day_date_attribute_${questionId}`, undefined)
                                    // }
                                })
                                .finally(() => {
                                    // refetchAllQuestions()
                                    formik.setSubmitting(false)
                                })
                        } else if (fieldWithoutId === 'attachment') {
                            const files = Array.isArray(value) ? value : []
                            setUploadingFilesCount(files.length)

                            const uploadSequentially = async () => {
                                for (const file of files) {
                                    try {
                                        const response = await updateAnswer({
                                            uid: storageData.postInitUid,
                                            questionId,
                                            value: file,
                                        }).unwrap()

                                        if (
                                            response?.data?.answer?.answer?.value &&
                                            Array.isArray(response.data.answer.answer.value)
                                        ) {
                                            formik.setFieldValue(field, response.data.answer.answer.value)
                                        }
                                    } catch (err: any) {
                                        const errors = err?.data?.errors
                                        const res = errors.map(({ msg }: { msg: string }) => msg).join(', ')
                                        // console.log('Upload error:', err?.data)
                                        if (res.length > 0) {
                                            formik.setFieldError(field, res)
                                        }
                                    } finally {
                                        setUploadingFilesCount((prev) => prev - 1)
                                    }
                                }
                            }

                            uploadSequentially().finally(() => {
                                setUploadingFilesCount(0)
                                formik.setSubmitting(false)
                                // refetchAllQuestions()
                            })
                        } else if (fieldWithoutId.includes('dateRange')) {
                            // const isEveryStartSetted = rangeDateTemplate.start.every((d) => !!d)
                            // const isEveryEndSetted = rangeDateTemplate.end.every((d) => !!d)
                            // // console.log(rangeDateTemplate, isEveryStartSetted, isEveryEndSetted)
                            // if (isEveryStartSetted && isEveryEndSetted) {
                            //     await updateAnswer({
                            //         uid: storageData.postInitUid,
                            //         questionId,
                            //         value: [rangeDateTemplate.start.join('-'), rangeDateTemplate.end.join('-')],
                            //     })
                            //         .unwrap()
                            //         .finally(() => {
                            //             // refetchAllQuestions()
                            //             formik.setSubmitting(false)
                            //         })
                            // }
                            await updateAnswer({
                                uid: storageData.postInitUid,
                                questionId,
                                value: value,
                            })
                                .unwrap()
                                .finally(() => {
                                    // refetchAllQuestions()
                                    formik.setSubmitting(false)
                                })
                        } else {
                            await updateAnswer({
                                uid: storageData.postInitUid,
                                questionId,
                                value: options?.convertValueToNumber
                                    ? value === '' || value === null || value === undefined
                                        ? null
                                        : Number(value)
                                    : value,
                            })
                                .unwrap()
                                .finally(() => {
                                    // refetchAllQuestions()
                                    formik.setSubmitting(false)
                                })
                        }
                        // console.log('Successfully updated field:', field, value)
                    } catch (error: any) {
                        const errors = error.data?.errors ?? []

                        console.log('Failed to update answer:', errors)
                        // TODO - add statement for attributes
                        if (fieldWithoutId.includes('attribute')) {
                            const newErrors = { ...formik.errors }

                            errors.forEach((err: any) => {
                                if (err.field?.includes('bool')) {
                                    newErrors[`bool_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`bool_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('rating')) {
                                    newErrors[`rating_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`rating_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('shortText')) {
                                    newErrors[`shortText_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`shortText_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('dropdown')) {
                                    newErrors[`dropdowns_attribute_${questionId}`] = err.msg
                                    formik.setFieldTouched(`dropdowns_attribute_${questionId}`, true, false)
                                }
                                if (err.field?.includes('date')) {
                                    formik.setFieldTouched(`date_attribute_${questionId}`, true, false)
                                    newErrors[`date_attribute_${questionId}`] = err.msg
                                }
                            })

                            formik.setErrors(newErrors)
                        } else if (fieldWithoutId.includes('number')) {
                            const newErrors = { ...formik.errors }
                            const errorMsg =
                                errors
                                    .map((x: { msg: string[]; code: number }) => {
                                        if (x.code !== 16) {
                                            return x.msg
                                        }
                                        return [getTitle('3924', 'Required field')]
                                    })
                                    .join(', ') ?? error.message
                            formik.setFieldError(field, errorMsg)
                            formik.setFieldTouched(field, true, false)
                        } else {
                            const errorMsg = errors.map((x: any) => x.msg).join(', ') ?? error.message
                            formik.setFieldError(field, errorMsg)
                            formik.setFieldTouched(field, true, false)
                        }
                    } finally {
                        delete debounceTimers.current[field]
                        // console.log(formik.values, formik.touched, formik.errors)
                        // console.log('FIIINALLL', tims, debounceTimers.current)
                        formik.setSubmitting(false)

                        removeLoadingItem(field)
                    }
                },
                500,
                debounceTimers.current
            )
        },
        [addLoadingItem, formik, storageData?.postInitUid, updateAnswer, getTitle, removeLoadingItem]
    )
    switch (question.type) {
        case 'singleSelect':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2SelectSingle
                        name={questionKey}
                        value={formik?.values[questionKey] as number | null}
                        options={question.options.map((option: any) => {
                            return { value: option.id, label: option.name }
                        })}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        placeholder={getTitle('10293', 'Select an option')}
                        onChange={(value: number | null | string) => {
                            submitDebounceValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                    </>
                </div>
            )

        case 'multiSelect':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2SelectMultiple
                        name={questionKey}
                        value={formik?.values[questionKey] as (string | number)[]}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        options={question.options.map((option: any) => ({
                            value: option.id,
                            label: option.name,
                        }))}
                        isRequired={question.required}
                        placeholder={getTitle('10293', 'Select an option')}
                        onChange={(vals) => {
                            submitDebounceValue(questionKey, vals)
                            // formik.setFieldValue(questionKey, vals)
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                    />
                    {Array.isArray(formik?.values[questionKey]) &&
                        (formik?.values[questionKey] as Array<number>).length > 0 && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                </div>
            )

        case 'text':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputText
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as string | number | boolean | undefined}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        placeholder={getTitle('10294', 'Enter your answer')}
                        onChange={(e) => {
                            const value = e.target.value
                            // formik.setFieldValue(questionKey, value)
                            submitDebounceValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        // isLoading={isLoading}
                        // maxLength={100}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                // parentQuestionId={question.questionId}
                                // parentOptionId={question.parentOptionId}
                                // parentValue={formik?.values[questionKey]}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                    </>
                </div>
            )

        case 'longText':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2Textarea
                        name={questionKey}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        // isLoading={isLoading}
                        value={formik?.values[questionKey] as string | null | undefined}
                        placeholder={getTitle('10294', 'Enter your answer')}
                        onChange={(v) => {
                            submitDebounceValue(questionKey, v)
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                    </>
                </div>
            )

        case 'yesNo':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputRadio
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as number | null}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        // options={[
                        //     { value: true, label: 'Yes' },
                        //     { value: false, label: 'No' },
                        // ]}
                        options={(question.options ?? [])
                            .map((option) => ({
                                label: option.name,
                                value: option.id,
                            }))
                            .sort((a, b) => {
                                if (a.label === 'Yes') return -1
                                if (b.label === 'Yes') return 1
                                return 0
                            })
                            .map((op) => ({
                                ...op,
                                label: op.label === 'Yes' ? getTitle('128', 'Yes') : getTitle('129', 'No'),
                            }))}
                        onChange={(value) => {
                            submitDebounceValue(questionKey, value)
                            // formik.setFieldValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        // isLoading={isLoading}
                    />
                    <>
                        {formik?.values[questionKey] && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                    </>
                </div>
            )

        case 'qualifying':
            return (
                <div className={`${baseClass}__question--item`}>
                    <div className={` ${baseClass}__qualifying`}>
                        <ApplyV2InputRadio
                            // id={question.questionId}
                            className={` ${baseClass}__qualifying--input`}
                            name={questionKey}
                            value={formik?.values[questionKey] as number | null}
                            label={decodeHtmlEntitiesRecursive(question.name)}
                            isRequired={question.required}
                            // options={[
                            //     { value: true, label: getTitle('128', 'Yes') },
                            //     { value: false, label: getTitle('129', 'No') },
                            // ]}
                            options={(question.options ?? [])
                                .map((option) => ({
                                    label: option.name,
                                    value: option.id,
                                }))
                                .sort((a, b) => {
                                    if (a.label === 'Yes') return -1
                                    if (b.label === 'Yes') return 1
                                    return 0
                                })
                                .map((op) => ({
                                    ...op,
                                    label: op.label === 'Yes' ? getTitle('128', 'Yes') : getTitle('129', 'No'),
                                }))}
                            onChange={(value) => {
                                // formik.setFieldValue(questionKey, value).then((errs) => {
                                // console.log(value, question.settings.qualifyingTrigger)
                                setIsQualifyingError(value === question.settings.qualifyingTrigger)
                                submitDebounceValue(questionKey, value)
                                // })
                            }}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                            // isLoading={isLoading}
                        />
                        {/*{formik.touched[questionKey] &&*/}
                        {formik?.values[questionKey] === question.settings.qualifyingTrigger && (
                            <div className={`${baseClass}__qualifying--trigger`}>
                                <div className={`${baseClass}__qualifying--trigger--top`}>
                                    <WarningSolidErrorIcon
                                        width={12}
                                        height={12}
                                        className={`${baseClass}__qualifying--trigger-icon`}
                                    />
                                    <span>{getTitle('7547', 'Please note')}:</span>
                                </div>
                                <div className={`${baseClass}__qualifying--trigger-message`}>
                                    {question.settings.message}
                                </div>
                                <ApplyV2Button
                                    className={`${baseClass}__qualifying--trigger-btn`}
                                    variant={'white'}
                                    size={'small'}
                                    leftIcon={<i className="fa-regular fa-xmark"></i>}
                                    onClick={onCloseApply}
                                >
                                    {getTitle('10245', 'Close application')}
                                </ApplyV2Button>
                            </div>
                        )}
                    </div>
                    <ApplyV2QuestionsList
                        questions={question.children}
                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                        refetchAllQuestions={refetchAllQuestions}
                        parent={{
                            parentQuestionId: question.questionId,
                            parentOptionId: question.parentOptionId,
                            parentValue: formik?.values[questionKey],
                            parentType: question.type,
                            customFieldType: null,
                        }}
                        addLoadingItem={addLoadingItem}
                        removeLoadingItem={removeLoadingItem}
                        setIsQualifyingError={setIsQualifyingError}
                    />
                </div>
            )

        case 'starRating':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputRate
                        // id={question.questionId}
                        name={questionKey}
                        value={formik?.values[questionKey] as number | null}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        onChange={(value) => {
                            submitDebounceValue(questionKey, value)
                            // formik.setFieldValue(questionKey, value)
                        }}
                        onBlur={formik.handleBlur}
                        // onFocus={() => {}}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        // isLoading={isLoading}
                    />

                    {Number(formik?.values[questionKey] ?? 0) > 0 && (
                        <ApplyV2QuestionsList
                            questions={question.children}
                            setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                            refetchAllQuestions={refetchAllQuestions}
                            parent={{
                                parentQuestionId: question.questionId,
                                parentOptionId: question.parentOptionId,
                                parentValue: formik?.values[questionKey],
                                parentType: question.type,
                                customFieldType: null,
                            }}
                            addLoadingItem={addLoadingItem}
                            removeLoadingItem={removeLoadingItem}
                            setIsQualifyingError={setIsQualifyingError}
                        />
                    )}
                </div>
            )

        case 'date':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2DatePickerInput
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        valueFormat={'YYYY-MM-DD'}
                        value={formik?.values[questionKey] as string}
                        name={questionKey}
                        onChange={(e) => {
                            submitDebounceValue(questionKey, e)
                        }}
                        onBlur={(e) => {
                            // console.log('blur', e)
                            formik.handleBlur(e)
                            formik.setFieldTouched(questionKey, true, true)
                        }}
                        isClearable={true}
                        // isDisabled={true}
                        // isLoading={true}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                    />

                    <ApplyV2QuestionsList
                        questions={question.children}
                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                        refetchAllQuestions={refetchAllQuestions}
                        parent={{
                            parentQuestionId: question.questionId,
                            parentOptionId: question.parentOptionId,
                            parentValue: formik?.values[questionKey],
                            parentType: question.type,
                            customFieldType: null,
                        }}
                        addLoadingItem={addLoadingItem}
                        removeLoadingItem={removeLoadingItem}
                        setIsQualifyingError={setIsQualifyingError}
                    />
                </div>
            )

        case 'dateRange':
            return (
                <div className={`${baseClass}__question--item`}>
                    <div className={`${baseClass}__question--item-dateRange`}>
                        <div className={`${baseClass}__question--item-dateRange-wrap`}>
                            <div className={`${baseClass}__question--item-dateRange-wrap-label`}>
                                {decodeHtmlEntitiesRecursive(question.name)}
                                {question.required && '*'}
                            </div>
                            <div className={`${baseClass}__question--item-dateRange-wrap-row`}>
                                <ApplyV2DatePickerInput
                                    // label={decodeHtmlEntitiesRecursive(question.name)}
                                    isRequired={question.required}
                                    placeholder={getTitle('9062', 'Set date')}
                                    valueFormat={'YYYY-MM-DD'}
                                    value={
                                        (formik?.values[questionKey] as [string, string])
                                            ? ((formik.values[questionKey] as [string, string])[0] as string)
                                            : null
                                    }
                                    onChange={(e) => {
                                        const endDate = (formik?.values[questionKey] as [string, string])
                                            ? ((formik.values[questionKey] as [string, string])[1] as string)
                                            : null
                                        formik.setFieldValue(questionKey, [e, endDate]).then(() => {
                                            if (endDate && e) {
                                                submitDebounceValue(questionKey, [e, endDate])
                                            }
                                        })
                                    }}
                                    onBlur={(e) => {
                                        formik.handleBlur(e)
                                        formik.setFieldTouched(questionKey, true, true)
                                    }}
                                    isClearable={true}
                                    // isDisabled={true}
                                    // isLoading={true}
                                    errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                    isShowErrorMessage={false}
                                />
                                <div className={`${baseClass}__question--item-dateRange-wrap-row-splitter`}></div>
                                <ApplyV2DatePickerInput
                                    // label={decodeHtmlEntitiesRecursive(question.name)}
                                    isRequired={question.required}
                                    placeholder={getTitle('326', 'End date')}
                                    valueFormat={'YYYY-MM-DD'}
                                    value={
                                        (formik?.values[questionKey] as [string, string])
                                            ? ((formik.values[questionKey] as [string, string])[1] as string)
                                            : null
                                    }
                                    onChange={(e) => {
                                        const startDate = (formik?.values[questionKey] as [string, string])
                                            ? ((formik.values[questionKey] as [string, string])[0] as string)
                                            : null
                                        formik.setFieldValue(questionKey, [startDate, e]).then(() => {
                                            if (startDate && e) {
                                                submitDebounceValue(questionKey, [startDate, e])
                                            }
                                        })
                                    }}
                                    onBlur={(e) => {
                                        formik.handleBlur(e)
                                        formik.setFieldTouched(questionKey, true, true)
                                    }}
                                    isClearable={true}
                                    // isDisabled={true}
                                    // isLoading={true}
                                    errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                    isShowErrorMessage={false}
                                />
                            </div>
                            {formik.touched[questionKey] && formik.errors[questionKey] && (
                                <div className={`${baseClass}__attribute__error`}>
                                    <WarningErrorIcon className={`${baseClass}__attribute__error--icon`} />
                                    <div className={`${baseClass}__attribute__error--text`}>
                                        {formik.errors[questionKey]}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <ApplyV2QuestionsList
                        questions={question.children}
                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                        refetchAllQuestions={refetchAllQuestions}
                        parent={{
                            parentQuestionId: question.questionId,
                            parentOptionId: question.parentOptionId,
                            parentValue: formik?.values[questionKey],
                            parentType: question.type,
                            customFieldType: null,
                        }}
                        addLoadingItem={addLoadingItem}
                        removeLoadingItem={removeLoadingItem}
                        setIsQualifyingError={setIsQualifyingError}
                    />
                </div>
            )

        case 'number':
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2InputNumber
                        name={questionKey}
                        value={formik?.values[questionKey] as number | undefined | null}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        placeholder={getTitle('10294', 'Enter your answer')}
                        onChange={(e) => {
                            submitDebounceValue(questionKey, e, {
                                convertValueToNumber: true,
                            })
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                        // isLoading={isLoading}
                    />
                    <>
                        {formik?.values[questionKey] != null && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                    </>
                </div>
            )

        case 'attachment':
            const files =
                (formik?.values[questionKey] as (
                    | ApplyV2TempBase64File
                    | ApplyV2FileAttachment
                    | ApplyV2TempCourseFile
                )[]) ?? []
            const isHideInput = question.settings?.multiple ? files.length >= 5 : files.length >= 1
            // console.log(isHideInput, 'isHideInput', files.length)
            return (
                <div className={`${baseClass}__question--item`}>
                    <ApplyV2AttachmentsUploader
                        name={questionKey}
                        label={decodeHtmlEntitiesRecursive(question.name)}
                        isRequired={question.required}
                        multipleCount={question.settings?.multiple ? 5 : 1}
                        isShowInput={!isHideInput}
                        onChange={(files) => {
                            submitDebounceValue(questionKey, files, { setFieldValueDefer: true })
                            // formik.setFieldValue(questionKey, processedFiles)
                        }}
                        maxFileSize={30}
                        accept={getAcceptFilesString('ATTACHMENTS')}
                        files={files}
                        onBlur={(e) => {
                            // console.log('On Blor here', e)
                            // formik.validateField(questionKey)
                            formik.handleBlur(e)
                        }}
                        onDelete={(f) => {
                            // const files = (formik.values[questionKey] as ApplyV2FileAttachment[]) ?? []
                            // submitDebounceValue(
                            //     questionKey,
                            //     files.filter((it) => it.name !== f.name)
                            // )
                            console.log('Delete', f)
                            if (!('uid' in f)) {
                                console.error('File does not have a UID and cannot be deleted.')
                                return
                            }
                            setLoadingFilesIds((prev) => {
                                return [...prev, f.uid]
                            })
                            const files = (formik.values[questionKey] as ApplyV2FileAttachment[]) ?? []
                            formik.setFieldValue(
                                questionKey,
                                files.filter((fff) => fff.uid !== f.uid)
                            )
                            deleteFileRequest({
                                uid: storageData?.postInitUid,
                                questionId: question.questionId,
                                fileUid: f.uid,
                            })
                                .unwrap()
                                .then((res) => {
                                    // const files = (formik.values[questionKey] as ApplyV2FileAttachment[]) ?? []
                                    // formik.setFieldValue(
                                    //     questionKey,
                                    //     files.filter((fff) => fff.uid !== f.uid)
                                    // )
                                })
                                .catch((err) => {
                                    console.log(err)
                                })
                                .finally(() => {
                                    setLoadingFilesIds((prev) => {
                                        return prev.filter((fi) => fi !== f.uid)
                                    })
                                })
                        }}
                        isLoading={isLoading}
                        loadingIds={loadingFilesIds}
                        uploadingCount={uploadingFilesCount}
                        errorMessage={
                            formik.touched[questionKey] &&
                            formik.errors[questionKey] &&
                            JSON.stringify(formik.errors[questionKey])
                        }
                    />

                    {Array.isArray(formik?.values[questionKey]) &&
                        (formik?.values[questionKey] as Array<any>).length > 0 && (
                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: null,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        )}
                </div>
            )

        case 'customField':
            switch (question.customField.typeId) {
                case ApplyV2CustomFieldTypeConst.SingleSelect:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2SelectSingle
                                name={questionKey}
                                value={formik?.values[questionKey] as number | null}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                options={(question.options ?? []).map((option: any) => ({
                                    value: option.id,
                                    label: option.name,
                                }))}
                                isRequired={question.required}
                                placeholder={getTitle('10293', 'Select an option')}
                                onChange={(value: number | null | string) => {
                                    submitDebounceValue(questionKey, value)
                                    // formik.setFieldValue(questionKey, value)
                                }}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                onBlur={formik.handleBlur}
                            />

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                        setIsQualifyingError={setIsQualifyingError}
                                    />
                                )}
                            </>
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.MultiSelect:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2SelectMultiple
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as (string | number)[]}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                options={(question.options ?? []).map((option: any) => ({
                                    value: option.id,
                                    label: option.name,
                                }))}
                                isRequired={question.required}
                                placeholder={getTitle('10293', 'Select an option')}
                                onChange={(vals) => {
                                    submitDebounceValue(questionKey, vals)
                                    // formik.setFieldValue(questionKey, vals)
                                }}
                                // onFocus={() => {}}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                // description={question.customField.description}
                                // isSearchable={true}
                                // isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            {Array.isArray(formik?.values[questionKey]) &&
                                (formik?.values[questionKey] as Array<any>).length > 0 && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                        setIsQualifyingError={setIsQualifyingError}
                                    />
                                )}
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.Text:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2InputText
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as string | number | boolean | undefined}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                placeholder={getTitle('10294', 'Enter your answer')}
                                onChange={(e) => {
                                    submitDebounceValue(questionKey, e.target.value)
                                    // formik.setFieldValue(questionKey, e.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                // description={question.customField.description}
                                // isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                        setIsQualifyingError={setIsQualifyingError}
                                    />
                                )}
                            </>
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.Integer:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2InputNumber
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as string | number}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                placeholder={getTitle('10294', 'Enter your answer')}
                                min={question.customField.properties?.min}
                                max={question.customField.properties?.max}
                                decimals={question.customField.properties?.decimals}
                                onChange={(value) => {
                                    submitDebounceValue(questionKey, value, { convertValueToNumber: true })
                                    // formik.setFieldValue(questionKey, value)
                                }}
                                onBlur={formik.handleBlur}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                currencyId={question.customField.properties?.currencyId}
                                // description={question.customField.description}
                                // isLoading={isLoading}
                                numberFormat={
                                    question.customField.properties?.numberFormat === 2 ? 'percent' : 'default'
                                }
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] != null && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: question.customField?.typeId,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                        setIsQualifyingError={setIsQualifyingError}
                                    />
                                )}
                            </>
                        </div>
                    )
                case ApplyV2CustomFieldTypeConst.Date:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2DatePickerInput
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                valueFormat={'YYYY-MM-DD'}
                                value={formik?.values[questionKey] as string}
                                onChange={(e) => {
                                    submitDebounceValue(questionKey, e)
                                }}
                                onBlur={(e) => {
                                    formik.handleBlur(e)
                                    formik.setFieldTouched(questionKey, true, true)
                                }}
                                isClearable={true}
                                // isDisabled={true}
                                // isLoading={true}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                            />

                            <ApplyV2QuestionsList
                                questions={question.children}
                                setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                refetchAllQuestions={refetchAllQuestions}
                                parent={{
                                    parentQuestionId: question.questionId,
                                    parentOptionId: question.parentOptionId,
                                    parentValue: formik?.values[questionKey],
                                    parentType: question.type,
                                    customFieldType: question.customField?.typeId,
                                }}
                                addLoadingItem={addLoadingItem}
                                removeLoadingItem={removeLoadingItem}
                                setIsQualifyingError={setIsQualifyingError}
                            />
                        </div>
                    )
                default:
                    return (
                        <div className={`${baseClass}__question--item`}>
                            <ApplyV2InputText
                                // id={question.questionId}
                                name={questionKey}
                                value={formik?.values[questionKey] as string | number | boolean | undefined}
                                label={decodeHtmlEntitiesRecursive(question.name)}
                                isRequired={question.required}
                                placeholder={getTitle('10294', 'Enter your answer')}
                                onChange={(e) => {
                                    submitDebounceValue(questionKey, e.target.value)
                                    // formik.setFieldValue(questionKey, e.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                // onFocus={() => {}}
                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                // isLoading={isLoading}
                            />
                            {/*{question.customField.description && <div>{question.customField.description}</div>}*/}

                            <>
                                {formik?.values[questionKey] && (
                                    <ApplyV2QuestionsList
                                        questions={question.children}
                                        setIsErrorUpdateAnswer={setIsErrorUpdateAnswer}
                                        refetchAllQuestions={refetchAllQuestions}
                                        parent={{
                                            parentQuestionId: question.questionId,
                                            parentOptionId: question.parentOptionId,
                                            parentValue: formik?.values[questionKey],
                                            parentType: question.type,
                                            customFieldType: null,
                                        }}
                                        addLoadingItem={addLoadingItem}
                                        removeLoadingItem={removeLoadingItem}
                                        setIsQualifyingError={setIsQualifyingError}
                                    />
                                )}
                            </>
                        </div>
                    )
            }

        case 'attribute':
            return (
                <div className={`${baseClass}__question--item ${baseClass}__attribute`}>
                    <div className={`${baseClass}__attribute--title`}>
                        {decodeHtmlEntitiesRecursive(question.name)}
                        {question.required && '*'}
                    </div>

                    {question.attribute.bool && (
                        <ApplyV2InputRadio
                            // id={`${question.questionId}--bool`}
                            name={`bool_${questionKey}`}
                            // className={`${baseClass}__question--item`}
                            className={`${baseClass}__attribute--data`}
                            value={formik?.values[`bool_${questionKey}`] as number | null}
                            options={[
                                { value: true, label: getTitle('128', 'Yes') },
                                { value: false, label: getTitle('129', 'No') },
                            ]}
                            onChange={(value) => {
                                submitDebounceValue(`bool_${questionKey}`, value)
                                // formik.setFieldValue(`bool_${questionKey}`, value)
                            }}
                            isRequired={question.required}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={formik.touched[`bool_${questionKey}`] && formik.errors[`bool_${questionKey}`]}
                            // isLoading={isLoading}
                        />
                    )}
                    {question.attribute.rating && (
                        <ApplyV2InputRate
                            // id={`${question.questionId}--rating`}
                            className={`${baseClass}__attribute--data`}
                            name={`rating_${questionKey}`}
                            value={formik?.values[`rating_${questionKey}`] as number | null}
                            onChange={(e) => {
                                submitDebounceValue(`rating_${questionKey}`, e)
                                // formik.setFieldValue(`rating_${questionKey}`, e)
                            }}
                            onBlur={formik.handleBlur}
                            errorMessage={
                                formik.touched[`rating_${questionKey}`] && formik.errors[`rating_${questionKey}`]
                            }
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.dropdown && question.attribute.dropdowns && (
                        <ApplyV2SelectSingle
                            // id={`${question.questionId}--dropdown`}
                            name={`dropdowns_${questionKey}`}
                            className={`${baseClass}__attribute--data`}
                            value={formik?.values[`dropdowns_${questionKey}`] as number | null}
                            options={question.attribute.dropdowns.map((d: any) => ({
                                value: d.id,
                                label: d.name,
                            }))}
                            isRequired={question.required}
                            placeholder={getTitle('10293', 'Select an option')}
                            onChange={(value: number | null | string) => {
                                submitDebounceValue(`dropdowns_${questionKey}`, value)
                                // formik.setFieldValue(`dropdowns_${questionKey}`, value)
                            }}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={
                                formik.touched[`dropdowns_${questionKey}`] && formik.errors[`dropdowns_${questionKey}`]
                            }
                            // isSearchable={true}
                            // isLoading={isLoading}
                        />
                    )}
                    {question.attribute.checkbox && question.attribute.checkboxes && (
                        <div>
                            <div className={`${baseClass}__attribute--data ${baseClass}__attribute--data-checkboxes`}>
                                {question.attribute.checkboxes.map((checkbox) => {
                                    return (
                                        <div
                                            key={checkbox.id}
                                            className={` ${baseClass}__attribute--data-checkboxes-item`}
                                        >
                                            <ApplyV2InputCheckbox
                                                // id={checkbox.id}\
                                                className={` ${baseClass}__attribute--data-checkboxes-item-input`}
                                                name={`checkboxes_${questionKey}`}
                                                value={
                                                    (Array.isArray(formik?.values[`checkboxes_${questionKey}`])
                                                        ? (formik.values[
                                                              `checkboxes_${questionKey}`
                                                          ] as AttributesCheckboxesForm[])
                                                        : []
                                                    ).find((it) => it.id === checkbox.id)?.checked
                                                }
                                                label={decodeHtmlEntitiesRecursive(checkbox.name)}
                                                onChange={(value) => {
                                                    const currentValues = formik.values[`checkboxes_${questionKey}`]
                                                    const checkboxesArray = Array.isArray(currentValues)
                                                        ? (currentValues as AttributesCheckboxesForm[])
                                                        : []

                                                    const values = checkboxesArray.map((it) => {
                                                        if (it.id === checkbox.id)
                                                            return {
                                                                ...it,
                                                                checked: value,
                                                                rating: !value ? 0 : it.rating,
                                                            }
                                                        return it
                                                    })
                                                    // formik.setFieldValue(`checkboxes_${questionKey}`, values)
                                                    submitDebounceValue(`checkboxes_${questionKey}`, values)
                                                }}
                                                onBlur={formik.handleBlur}
                                                errorMessage={formik.touched[questionKey] && formik.errors[questionKey]}
                                                // isLoading={isLoading}
                                            />
                                            {question.attribute.checkboxRating && (
                                                <ApplyV2InputRate
                                                    className={` ${baseClass}__attribute--data-checkboxes-item-rating`}
                                                    name={`checkboxes_${questionKey}`}
                                                    value={
                                                        (Array.isArray(formik?.values[`checkboxes_${questionKey}`])
                                                            ? (formik.values[
                                                                  `checkboxes_${questionKey}`
                                                              ] as AttributesCheckboxesForm[])
                                                            : []
                                                        ).find((it) => it.id === checkbox.id)?.rating
                                                    }
                                                    onChange={(value) => {
                                                        const currentValues = formik.values[`checkboxes_${questionKey}`]
                                                        const checkboxesArray = Array.isArray(currentValues)
                                                            ? (currentValues as AttributesCheckboxesForm[])
                                                            : []
                                                        const values = checkboxesArray.map((it) => {
                                                            if (it.id === checkbox.id)
                                                                return { ...it, rating: value, checked: true }
                                                            return it
                                                        })
                                                        // formik.setFieldValue(`checkboxes_${questionKey}`, values)
                                                        submitDebounceValue(`checkboxes_${questionKey}`, values)
                                                    }}
                                                    // isDisabled={
                                                    //     (Array.isArray(formik?.values[`checkboxes_${questionKey}`])
                                                    //         ? (formik.values[
                                                    //               `checkboxes_${questionKey}`
                                                    //           ] as AttributesCheckboxesForm[])
                                                    //         : []
                                                    //     ).find((it) => it.id === checkbox.id)?.checked !== true
                                                    // }
                                                    onBlur={formik.handleBlur}
                                                    // isLoading={isLoading}
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                            {formik.touched[`checkboxes_${questionKey}`] &&
                                formik.errors[`checkboxes_${questionKey}`] && (
                                    <div className={`${baseClass}__attribute__error`}>
                                        <WarningErrorIcon className={`${baseClass}__attribute__error--icon`} />
                                        <div className={`${baseClass}__attribute__error--text`}>
                                            {formik.errors[`checkboxes_${questionKey}`]}
                                        </div>
                                    </div>
                                )}
                        </div>
                    )}
                    {question.attribute.text && (
                        <ApplyV2Textarea
                            className={`${baseClass}__attribute--data`}
                            name={`text_${questionKey}`}
                            value={formik?.values[`text_${questionKey}`] as string}
                            isRequired={question.required}
                            placeholder={getTitle('10294', 'Enter your answer')}
                            onChange={(v) => {
                                submitDebounceValue(`text_${questionKey}`, v)
                            }}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched[`text_${questionKey}`] && formik.errors[`text_${questionKey}`]}
                        />
                    )}
                    {question.attribute.shortText && (
                        <ApplyV2InputText
                            // id={`${question.questionId}--shortText`}
                            name={`shortText_${questionKey}`}
                            className={`${baseClass}__attribute--data`}
                            value={formik?.values[`shortText_${questionKey}`] as string}
                            placeholder={getTitle('10294', 'Enter your answer')}
                            isRequired={question.required}
                            onChange={(e) => {
                                submitDebounceValue(`shortText_${questionKey}`, e.target.value)
                                // formik.setFieldValue(`shortText_${questionKey}`, e.target.value)
                            }}
                            onBlur={formik.handleBlur}
                            // onFocus={() => {}}
                            errorMessage={
                                formik.touched[`shortText_${questionKey}`] && formik.errors[`shortText_${questionKey}`]
                            }
                            // isLoading={isLoading}
                        />
                    )}

                    {question.attribute.date && (
                        <ApplyV2DatePickerInput
                            className={`${baseClass}__attribute--data`}
                            // label={decodeHtmlEntitiesRecursive(question.name)}
                            name={`date_${questionKey}`}
                            isRequired={question.required}
                            valueFormat={'YYYY-MM-DD'}
                            value={formik?.values[`date_${questionKey}`] as string}
                            onChange={(e) => {
                                submitDebounceValue(`date_${questionKey}`, e)
                            }}
                            onBlur={(e) => {
                                formik.handleBlur(e)
                                formik.setFieldTouched(`date_${questionKey}`, true, true)
                            }}
                            isClearable={true}
                            // isDisabled={true}
                            // isLoading={true}
                            errorMessage={formik.touched[`date_${questionKey}`] && formik.errors[`date_${questionKey}`]}
                        />
                    )}
                </div>
            )

        default:
            return null
    }
}

export default ApplyV2QuestionsItem
